import type {
  CartCodeDiscountAllocation,
  GetCartQuery,
} from 'services/shopify';
import { CurrencyCode } from 'services/shopify';
import type { Cart } from 'types/cart/Cart';
import type { CartLineItem } from 'types/cart/CartLineItem';
import type { CustomAttributes } from 'types/cart/CustomAttributes';
import type { PreUpsellLineItem } from 'types/cart/PreUpsellLineItem';
import type { Money } from 'types/product/Money';
import { convertToNumber } from 'utils/convertToNumber';
import { formatShopifyPrice } from 'utils/formatShopifyPrice';
import {
  cleanDiscountCodes,
  getCartTotalDiscountAmount,
  mergeCartDiscountAllocations,
} from 'utils/shopifyCart';

const defaultPrice: Money = {
  amount: 0,
  currencyCode: CurrencyCode.Usd,
};

export const shopifyVariantGID = 'gid://shopify/ProductVariant/';

/* INFO: A preUpsellLineItem is the original line item */
function getPreUpsellLineItem(attributes: CustomAttributes) {
  const preUpsellStringified = attributes.find(
    (a) => a.key === '__timeline_upsell_originalCartLine',
  )?.value;

  return preUpsellStringified
    ? (JSON.parse(preUpsellStringified) as PreUpsellLineItem)
    : null;
}

export const mapShopifyCart = (
  cart: NonNullable<GetCartQuery['cart']>,
): Cart => {
  const cartDiscounts = mergeCartDiscountAllocations({
    discountAllocations:
      cart.discountAllocations as CartCodeDiscountAllocation[],
  });

  // NOTE: The discount code is converted to uppercase to ensure that the
  // discount code is not duplicated when the user enters a discount code in
  // uppercase. The discountCodes are then deduped using the code to make sure
  // we only render them once.
  const discountCodes = cleanDiscountCodes({
    discountCodes: cart.discountCodes,
  });

  const totalDiscountAmount = getCartTotalDiscountAmount({
    discountAllocations: cartDiscounts,
  });

  const lines: CartLineItem[] = cart.lines.edges.map(({ node }) => {
    const attributes = node.attributes as CustomAttributes;
    const discountAllocations = node.discountAllocations.filter(
      (item) => 'code' in item && 'discountedAmount' in item,
    ) as CartCodeDiscountAllocation[];

    const preUpsellLineItem = getPreUpsellLineItem(attributes);

    return {
      id: node.id,
      quantity: node.quantity,
      preUpsellLineItem,
      discountAllocations: discountAllocations.map((item) => ({
        code: item.code,
        discountedAmount: formatShopifyPrice(item.discountedAmount),
      })),
      merchandise: {
        id: node.merchandise.id,
        title: node.merchandise.title,
        availableForSale: node.merchandise.availableForSale,
        price: formatShopifyPrice(node.merchandise.price),
        product: {
          id: node.merchandise.product.id,
          handle: node.merchandise.product.handle,
          title: node.merchandise.product.title,
          hasVariants: node.merchandise.product.variants.edges.length > 1,
          collections: node.merchandise.product.collections.edges.map(
            ({ node: c }) => c.handle,
          ),
        },
        image: node.merchandise?.image?.url
          ? {
              altText: node.merchandise?.image?.altText || '',
              url: node.merchandise?.image?.url as string,
            }
          : undefined,
        sku: node.merchandise.sku || null,
        servings: convertToNumber(node.merchandise?.servings?.value),
        maxQuantity: convertToNumber(node.merchandise.maxQuantity?.value),
      },
      sellingPlanAllocation: node.sellingPlanAllocation?.sellingPlan.id
        ? {
            priceAdjustments: node.sellingPlanAllocation.priceAdjustments.map(
              (adjustments) => ({
                price: formatShopifyPrice(adjustments.price),
              }),
            ) as { price: Money }[],
            sellingPlan: {
              id: node.sellingPlanAllocation.sellingPlan.id,
              title: node.sellingPlanAllocation.sellingPlan.name,
            },
          }
        : null,
      cost: {
        subtotalAmount: formatShopifyPrice(node.cost.subtotalAmount),
        totalAmount: formatShopifyPrice(node.cost.totalAmount),
      },
    };
  });

  const linesDiscounts = lines.map((line) => line.discountAllocations);
  const totalLinesDiscountAmount = getCartTotalDiscountAmount({
    discountAllocations: linesDiscounts.flat(),
  });

  return {
    id: cart.id,
    totalQuantity: cart.totalQuantity,
    attributes: cart.attributes.flatMap(({ key, value }) =>
      value ? [{ key, value }] : [],
    ),
    lines: lines || [],
    checkoutUrl: cart.checkoutUrl as string,
    discountCodes: discountCodes || [],
    discountAllocations: cartDiscounts || [],
    cost: {
      subtotalAmount: formatShopifyPrice(cart.cost?.subtotalAmount),
      totalAmount: formatShopifyPrice(cart.cost?.totalAmount),
      totalTaxAmount: cart.cost?.totalTaxAmount
        ? formatShopifyPrice(cart.cost?.totalTaxAmount)
        : null,
    },
    subTotal: formatShopifyPrice({
      amount:
        Number(cart.cost?.subtotalAmount.amount) + totalLinesDiscountAmount,
      currencyCode: cart.cost?.subtotalAmount?.currencyCode,
    }),
    totalPrice:
      formatShopifyPrice({
        amount: Number(cart.cost?.subtotalAmount.amount) - totalDiscountAmount,
        currencyCode: cart.cost?.totalAmount?.currencyCode,
      }) || defaultPrice,
  };
};
